import { useCallback, useEffect, useState } from 'react';
import {
  type AdminMetrics,
  type AdminOrg,
  type AdminUser,
  type CouponDiscountType,
  type CouponInfo,
  createCoupon,
  deactivateCoupon,
  fetchAdminMetrics,
  fetchPlans,
  impersonateOrg,
  isSelfServicePlan,
  listAdminOrgs,
  listAdminUsers,
  listCoupons,
  type PlanInfo,
} from '../services/api';
import { useT } from '../i18n';

// Issue #332: painel admin global para super_user.
// Consome /api/admin/orgs, /api/admin/users, /api/admin/metrics.
// Guard de super_user e feito no App.tsx antes de montar esta pagina.

// Issue #550 (feat/audit-gestao): a tab "Auditoria LGPD" (issue #546) saiu
// deste painel — virou pagina propria no grupo "Gestao" do menu, visivel
// para os tenants (AUDIT_ROLES). O painel volta a ter uma unica visao.

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

function roleLabel(role: string, t: (k: string) => string): string {
  if (role === 'super_user') return t('admin.role.super_user');
  if (role === 'admin' || role === 'owner') return t('admin.role.admin');
  if (role === 'contador') return t('admin.role.contador');
  return t('admin.role.member');
}

function orgStatusBadge(status: string): string {
  if (status === 'suspended' || status === 'overdue') return 'bg-red-100 text-red-700';
  if (status === 'trial') return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
}

function MetricCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border border-brand-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-brand-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-brand-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-brand-400">{hint}</p>}
    </div>
  );
}

// Barra CSS simples (sem dependencia de charts) para erro rate.
function ErrorRateBar({ rate }: { rate: number }) {
  // rate já é uma porcentagem (0-100) vinda do backend (Issue #345).
  const pct = Math.min(100, Math.max(0, Math.round(rate)));
  const color = pct >= 50 ? 'bg-red-500' : pct >= 20 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-brand-100">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Issue #544: gestao de cupons de desconto (super_user only — esta pagina
// inteira ja e gated por user.role === 'super_user' no App.tsx).
// ---------------------------------------------------------------------------

function couponValueLabel(c: CouponInfo): string {
  return c.discount_type === 'percent'
    ? `${c.value}%`
    : `R$ ${c.value.toFixed(2)}`;
}

function CouponsSection() {
  const t = useT();
  const [coupons, setCoupons] = useState<CouponInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percent' as CouponDiscountType,
    value: '',
    max_uses: '',
    max_uses_per_org: '1',
    expires_at: '',
    // Issue #593/F2: planos elegiveis derivados da API (self-service:
    // pagos, nao-free, nao contact-only) — 'single'/'team' apos #592,
    // 'pro' enquanto o backend legado nao e migrado. Nunca hardcode.
    eligible_plans: [] as string[],
  });
  const [planOptions, setPlanOptions] = useState<PlanInfo[]>([]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCoupons();
      setCoupons(res.coupons ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.coupons.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCoupons();
    // Review F2 (PR #595): opcoes de plano elegivel vem da API — com o
    // backend legado aparece 'pro'; com o contrato #592, 'single'/'team'.
    fetchPlans()
      .then((res) => setPlanOptions(res.plans.filter(isSelfServicePlan)))
      .catch(() => setPlanOptions([]));
  }, [fetchCoupons]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await createCoupon({
        code: form.code,
        discount_type: form.discount_type,
        value: Number(form.value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        max_uses_per_org: Number(form.max_uses_per_org) || 1,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        // Sem plano marcado = todos os planos pagos (null no backend).
        eligible_plans: form.eligible_plans.length > 0 ? form.eligible_plans : null,
      });
      setForm({
        code: '',
        discount_type: 'percent',
        value: '',
        max_uses: '',
        max_uses_per_org: '1',
        expires_at: '',
        eligible_plans: [],
      });
      await fetchCoupons();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.coupons.error.create'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(coupon: CouponInfo) {
    setError(null);
    try {
      await deactivateCoupon(coupon.coupon_id);
      await fetchCoupons();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.coupons.error.create'));
    }
  }

  const inputCls =
    'rounded-md border border-brand-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <div className="mt-8">
      <h2 className="mb-2 text-lg font-semibold text-brand-800">{t('admin.coupons.title')}</h2>
      <p className="mb-4 text-sm text-brand-500">{t('admin.coupons.subtitle')}</p>

      {error && (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {/* Formulario de criacao */}
      <form onSubmit={handleCreate} className="mb-6 grid gap-3 rounded-md border border-brand-100 bg-brand-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-code">
            {t('admin.coupons.form.code')}
          </label>
          <input
            id="coupon-code"
            required
            minLength={8}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            className={inputCls}
            placeholder="CAMPANHA-2026-X4K9"
          />
          <p className="text-xs text-brand-400">{t('admin.coupons.form.codeHint')}</p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-type">
            {t('admin.coupons.form.discountType')}
          </label>
          <select
            id="coupon-type"
            value={form.discount_type}
            onChange={(e) => setForm({ ...form, discount_type: e.target.value as CouponDiscountType })}
            className={inputCls}
          >
            <option value="percent">{t('admin.coupons.typePercent')}</option>
            <option value="fixed">{t('admin.coupons.typeFixed')}</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-value">
            {t('admin.coupons.form.value')}
          </label>
          <input
            id="coupon-value"
            required
            type="number"
            min="0"
            step="0.01"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className={inputCls}
            placeholder={form.discount_type === 'percent' ? '20' : '50.00'}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-max-uses">
            {t('admin.coupons.form.maxUses')}
          </label>
          <input
            id="coupon-max-uses"
            type="number"
            min="1"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            className={inputCls}
            placeholder={t('admin.coupons.unlimited')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-max-org">
            {t('admin.coupons.form.maxUsesPerOrg')}
          </label>
          <input
            id="coupon-max-org"
            type="number"
            min="1"
            value={form.max_uses_per_org}
            onChange={(e) => setForm({ ...form, max_uses_per_org: e.target.value })}
            className={inputCls}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-brand-600" htmlFor="coupon-expires">
            {t('admin.coupons.form.expiresAt')}
          </label>
          <input
            id="coupon-expires"
            type="date"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            className={inputCls}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-brand-600">{t('admin.coupons.form.eligiblePlans')}</span>
          <div className="flex items-center gap-3 pt-1.5">
            {planOptions.map((p) => (
              <label key={p.plan} className="flex items-center gap-1 text-sm text-brand-700">
                <input
                  type="checkbox"
                  checked={form.eligible_plans.includes(p.plan)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      eligible_plans: e.target.checked
                        ? [...form.eligible_plans, p.plan]
                        : form.eligible_plans.filter((k) => k !== p.plan),
                    })
                  }
                  className="h-4 w-4 rounded border-brand-300 text-brand-600"
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60"
          >
            {saving ? t('admin.coupons.form.saving') : t('admin.coupons.form.submit')}
          </button>
        </div>
      </form>

      {/* Lista de cupons */}
      {loading ? (
        <p className="text-sm text-brand-600">{t('admin.loading')}</p>
      ) : coupons.length === 0 ? (
        <p className="text-sm text-brand-500">{t('admin.coupons.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-brand-200 text-brand-700">
              <tr>
                <th className="py-2 pr-4">{t('admin.coupons.col.code')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.type')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.value')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.uses')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.expires')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.plans')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.status')}</th>
                <th className="py-2 pr-4">{t('admin.coupons.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.coupon_id} className="border-b border-brand-100">
                  <td className="py-2 pr-4 font-medium text-brand-800">{c.code}</td>
                  <td className="py-2 pr-4 text-brand-600">
                    {c.discount_type === 'percent'
                      ? t('admin.coupons.typePercent')
                      : t('admin.coupons.typeFixed')}
                  </td>
                  <td className="py-2 pr-4 text-brand-600">{couponValueLabel(c)}</td>
                  <td className="py-2 pr-4 text-brand-600">
                    {c.uses_count}/{c.max_uses ?? t('admin.coupons.unlimited')}
                  </td>
                  <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(c.expires_at)}</td>
                  <td className="py-2 pr-4 text-xs text-brand-500">
                    {c.eligible_plans?.join(', ') || t('admin.coupons.allPlans')}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${
                        c.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {c.is_active ? t('admin.coupons.active') : t('admin.coupons.inactive')}
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    {c.is_active && (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(c)}
                        className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                      >
                        {t('admin.coupons.deactivate')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminPanel() {
  const t = useT();
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Issue #345: por default esconde dados de teste (is_test=true).
  const [includeTest, setIncludeTest] = useState(false);
  // Issue #330: impersonation ("acessar como" tenant).
  const [impersonateTarget, setImpersonateTarget] = useState<AdminOrg | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const handleImpersonate = useCallback(async () => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    setImpersonateError(null);
    try {
      await impersonateOrg(impersonateTarget.id);
      // Recarrega a pagina para entrar no modo impersonation.
      window.location.reload();
    } catch (err) {
      setImpersonateError(err instanceof Error ? err.message : 'Erro ao iniciar impersonation.');
    } finally {
      setImpersonating(false);
    }
  }, [impersonateTarget]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgsRes, usersRes, metricsRes] = await Promise.all([
        listAdminOrgs(includeTest),
        listAdminUsers(includeTest),
        fetchAdminMetrics(includeTest),
      ]);
      setOrgs(orgsRes.orgs ?? []);
      setUsers(usersRes.users ?? []);
      setMetrics(metricsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t, includeTest]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Issue #345: o backend já retorna error_rate como porcentagem (0-100);
  // não multiplicar por 100 novamente (causava 2818% = 28.18 * 100).
  const errorRatePct = metrics ? metrics.error_rate.toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-brand-900">{t('admin.title')}</h1>
          <p className="text-sm text-brand-600">{t('admin.subtitle')}</p>
        </div>

        {error && (
          <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <label className="mb-4 flex items-center gap-2 text-sm text-brand-700">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            className="h-4 w-4 rounded border-brand-300 text-brand-600 focus:ring-brand-500"
          />
          {t('admin.showTest')}
        </label>

        {loading ? (
          <p className="text-sm text-brand-600">{t('admin.loading')}</p>
        ) : (
          <>
            {/* Metricas globais */}
            <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              <MetricCard label={t('admin.metric.totalDocs')} value={metrics?.total_docs ?? 0} />
              <MetricCard label={t('admin.metric.totalUsers')} value={metrics?.total_users ?? 0} />
              <MetricCard label={t('admin.metric.totalOrgs')} value={metrics?.total_orgs ?? 0} />
              <MetricCard
                label={t('admin.metric.activeOrgs')}
                value={metrics?.active_orgs ?? 0}
                hint={`${metrics?.total_orgs ?? 0} ${t('admin.metric.totalOrgsHint')}`}
              />
              <div className="rounded-lg border border-brand-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-brand-500">
                  {t('admin.metric.errorRate')}
                </p>
                <p className="mt-1 text-2xl font-bold text-brand-900">{errorRatePct}%</p>
                <div className="mt-2">
                  <ErrorRateBar rate={metrics?.error_rate ?? 0} />
                </div>
                <p className="mt-1 text-xs text-brand-400">
                  {metrics?.total_errors ?? 0} {t('admin.metric.errors')}
                </p>
              </div>
            </div>

            {/* Tabela de orgs */}
            <h2 className="mb-2 text-lg font-semibold text-brand-800">{t('admin.orgs.title')}</h2>
            {orgs.length === 0 ? (
              <p className="mb-6 text-sm text-brand-500">{t('admin.orgs.empty')}</p>
            ) : (
              <div className="mb-6 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-brand-200 text-brand-700">
                    <tr>
                      <th className="py-2 pr-4">{t('admin.orgs.col.name')}</th>
                      <th className="py-2 pr-4">{t('admin.orgs.col.docs')}</th>
                      <th className="py-2 pr-4">{t('admin.orgs.col.users')}</th>
                      <th className="py-2 pr-4">{t('admin.orgs.col.status')}</th>
                      <th className="py-2 pr-4">{t('admin.orgs.col.created')}</th>
                      <th className="py-2 pr-4">{t('admin.orgs.col.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => (
                      <tr key={o.id} className="border-b border-brand-100">
                        <td className="py-2 pr-4 text-brand-800">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{o.name}</span>
                            {o.is_test && (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                                {t('admin.testBadge')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-brand-400">{o.slug}</div>
                        </td>
                        <td className="py-2 pr-4 text-brand-600">{o.docs_count}</td>
                        <td className="py-2 pr-4 text-brand-600">{o.users_count}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${orgStatusBadge(o.status)}`}>
                            {o.status || 'active'}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(o.created_at)}</td>
                        <td className="py-2 pr-4">
                          <button
                            type="button"
                            onClick={() => setImpersonateTarget(o)}
                            className="rounded-md bg-purple-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-purple-700"
                          >
                            {t('admin.impersonate')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tabela de usuarios */}
            <h2 className="mb-2 text-lg font-semibold text-brand-800">{t('admin.users.title')}</h2>
            {users.length === 0 ? (
              <p className="text-sm text-brand-500">{t('admin.users.empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-brand-200 text-brand-700">
                    <tr>
                      <th className="py-2 pr-4">{t('admin.users.col.email')}</th>
                      <th className="py-2 pr-4">{t('admin.users.col.org')}</th>
                      <th className="py-2 pr-4">{t('admin.users.col.role')}</th>
                      <th className="py-2 pr-4">{t('admin.users.col.status')}</th>
                      <th className="py-2 pr-4">{t('admin.users.col.created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-brand-100">
                        <td className="py-2 pr-4 text-brand-800">{u.email}</td>
                        <td className="py-2 pr-4 text-brand-600">{u.org_name || t('common.dash')}</td>
                        <td className="py-2 pr-4">
                          <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                            {roleLabel(u.role, t)}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {u.is_active ? t('admin.users.active') : t('admin.users.disabled')}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Issue #544: gestao de cupons de desconto (super_user). */}
            <CouponsSection />
          </>
        )}
      </div>
      {/* Issue #330: Modal de confirmacao de impersonation. */}
      {impersonateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold text-brand-800">
              {t('impersonation.confirmTitle')}
            </h2>
            <p className="mb-4 text-sm text-brand-600">
              {t('impersonation.confirmBody', { name: impersonateTarget.name })}
            </p>
            {impersonateError && (
              <p className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                {impersonateError}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setImpersonateTarget(null); setImpersonateError(null); }}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('impersonation.cancel')}
              </button>
              <button
                type="button"
                onClick={handleImpersonate}
                disabled={impersonating}
                className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {impersonating ? t('impersonation.starting') : t('impersonation.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
